import { User } from 'interfaces/user'
import { Designer } from 'interfaces/designer'
import { Company } from './company'
import { TicketFeedbackValues } from './ticket_feedback'
import { Skill } from 'lib/api/skills/skills'
import { CompanyTag } from 'lib/api/company-tags/company-tags'
import { Project, ProjectStatus } from './project'
import { Brand } from './brand'
import { CustomerState, ImageableType, TicketFile } from 'lib/api/ticket-files/ticket-files'

export type Thumbnail = {
  url: string
  placeholder: boolean
}

// Sometimes this is used as a "Request" type from the BE and sometimes as a "Ticket" type
export interface Ticket {
  brand?: Brand
  category: string
  company?: Company
  companyId?: bigint
  currentVersion?: number
  designer?: Designer
  feedbackReasons?: string[]
  filesDownloadUrl: string
  downloadableFiles: { name: string; url: string }[]
  friendlyStatusName: string
  id: bigint
  incompleteLabel?: string
  index?: number
  lastUpdatedForUser?: string
  maxTimeToSpend?: number
  newRequestExperience: boolean
  overallRating: TicketFeedbackValues
  position: number
  project: Project
  requestId: bigint
  skillName: string
  stateChangedAt?: string
  status: string
  subject: string
  tags: CompanyTag[]
  thumbnail: Thumbnail
  ticketId: bigint
  ticketFilesCount: bigint
  todaysWeightInMinutes?: number
  updatedAt?: string
  url: string
  user: User
}

export interface Asset {
  id: string
  name: string
}

export interface SearchTicketCompanyTag {
  id: number
  name: string
}

export interface SearchResponse<T> {
  data: T[]
  meta: {
    nextPage: string
    pageSize: number
    previousPage: string
    total: number
  }
}

export interface SearchTicket {
  assets?: Asset[]
  brand?: { id: number; name: string; colors: string[] }
  company: Company
  context: string
  designer: Designer
  display_status?: string // Only present in the SearchV2Ticket
  downloadableFiles: Ticket['downloadableFiles']
  files_download_url?: string
  project: Project
  skill: Skill
  thumbnail: Thumbnail
  ticket_company_tags: SearchTicketCompanyTag[]
  ticket_created_at: number
  ticket_created_by: SearchTicketUser
  ticket_deliverable_formats: string[]
  ticket_id: number
  ticket_state: number
  ticket_status: string
  ticket_updated_at: number
  title: string
}

export interface SearchV2Response<T> {
  hits: {
    total: {
      value: number
      relation: string
    }
    hits: [
      {
        _index: string
        _id: string
        _score: number
        _source: T
      },
    ]
  }
}

export interface SearchV2Ticket {
  brand_id: number
  brand_name: string
  brand_colors: string[]
  company_id: number
  company_name: string
  created_at: string
  current_version: number
  designer_avatar: string
  designer_id: number
  designer_name: string
  detail_tasks_description: string[]
  featured_file_s3_filename: string
  formats: string[]
  friendly_status: string
  id: number
  project_id: number
  project_name: string
  project_status: string
  skill_category: string
  skill_id: number
  skill_name: string
  state: string
  subject: string
  submitted_at: string
  thumb_rating: string | null
  thumbnail: Thumbnail
  ticket_company_tags: SearchTicketCompanyTag[]
  ticket_file_names: string[]
  type: string
  updated_at: string
  user_id: number
  user_name: string
}

interface SearchTicketUser {
  avatar: string
  id: number
  name: string
}

export interface SearchProject extends Project {
  project_status?: ProjectStatus
}

export interface SearchTicketFile {
  id: number
  assetable_id: number
  assetable_name: string
  assetable_type: string
  association_brand_id: number | null
  brand_id: number | null
  children: SearchTicketFile[]
  colorspace: string
  company_id: number
  company_tag_ids: number[]
  company_tag_names: string[]
  company_tags: SearchTicketCompanyTag[]
  created_at: string
  customer_state: string
  downloads: unknown[]
  dpi: number | null
  exported: boolean
  external_link: string | null
  file_size: number
  filename: string
  format: string
  gen_ai: boolean
  height: number
  history: unknown[]
  imageable_id: number
  imageable_type: string
  metadata: {
    user: {
      id: number
      name: string
    }
  }
  mimetype: string
  original_file_id: number
  parent_id: number | null
  preview: boolean
  project_id: number | null
  s3_filename: string
  s3_url: string | null
  shared: boolean
  state: string
  stock_asset: boolean
  ticket_id: number
  type: string
  updated_at: string
  uploaded_by_creative: boolean
  user_id: number
  user_name: number
  version: number
  width: number
  preview_url: string
}

export const convertSearchTicketFileToTicketFile = (searchTicketFile: SearchTicketFile): TicketFile => {
  return {
    id: searchTicketFile.id,
    name: searchTicketFile.filename,
    fileSize: searchTicketFile.file_size,
    url: searchTicketFile.s3_url,
    createdAt: searchTicketFile.created_at,
    previewUrl: searchTicketFile.preview_url,
    downloadUrl: searchTicketFile.s3_url,
    preview: searchTicketFile.preview,
    width: searchTicketFile.width,
    height: searchTicketFile.height,
    dpi: searchTicketFile.dpi,
    colorspace: searchTicketFile.colorspace,
    customerState: searchTicketFile.customer_state as CustomerState,
    uploadedByCreative: searchTicketFile.uploaded_by_creative,
    tags: searchTicketFile.company_tags as CompanyTag[],
    ticketId: searchTicketFile.ticket_id,
    annotations: [],
    errors: [],
    handle: '',
    imageableId: searchTicketFile.imageable_id,
    isExtractable: false,
    extractedFromZip: null,
    isVideo: false,
    mimetype: searchTicketFile.mimetype,
    ticketVersion: searchTicketFile.version,
    imageableType: searchTicketFile.imageable_type as ImageableType,
  }
}

export const convertSearchTicketFilesToTicketFiles = (files: SearchTicketFile[]): TicketFile[] => {
  return files.map(convertSearchTicketFileToTicketFile)
}
